import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Room } from '../types/user';
import {
  InviteContext,
  buildInviteLink,
  buildWhatsAppUrl,
  buildMailtoUrl,
  buildWebSharePayload,
  defaultInviteNote,
  roomThumbUrl,
} from '../utils/inviteLink';
import './ShareRoomModal.css';

interface ShareRoomModalProps {
  room: Room;
  /** Who is sending the invite (defaults handled by caller/user context). */
  inviterName?: string | null;
  onClose: () => void;
}

/**
 * Invite panel: editable note + who-invited, a live thumbnail preview of the
 * card recipients will see, and one-tap share to WhatsApp / Email / Copy /
 * native share sheet. All links carry ?from + ?note so the recipient sees who
 * invited them and lands straight in the room. The WhatsApp/social preview card
 * (room thumbnail) is produced by the server-rendered OG shim — see inviteLink.ts.
 */
export function ShareRoomModal({ room, inviterName, onClose }: ShareRoomModalProps) {
  const [note, setNote] = useState<string>(() => defaultInviteNote({ room, inviterName }));
  const [copied, setCopied] = useState(false);

  const ctx: InviteContext = useMemo(
    () => ({ room, inviterName, note }),
    [room, inviterName, note],
  );

  const link = useMemo(() => buildInviteLink(ctx), [ctx]);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select-less prompt is overkill; surface a soft failure state.
      setCopied(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(buildWhatsAppUrl(ctx), '_blank', 'noopener,noreferrer');
  };

  const handleEmail = () => {
    window.location.href = buildMailtoUrl(ctx);
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share(buildWebSharePayload(ctx));
    } catch {
      /* user cancelled — no-op */
    }
  };

  const thumb = roomThumbUrl(room);

  return createPortal(
    <div className="share-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="share-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Invite friends to ${room.name}`}
      >
        <button className="share-modal-close" onClick={onClose} aria-label="Close">×</button>

        <h2 className="share-modal-title">Invite to <span>{room.name}</span></h2>
        {inviterName && (
          <p className="share-modal-from">Inviting as <strong>{inviterName}</strong></p>
        )}

        {/* Preview of the card recipients will see in WhatsApp/social */}
        <div className="share-preview-card">
          <div
            className="share-preview-thumb"
            style={{ backgroundImage: `url(${thumb})` }}
            aria-hidden="true"
          />
          <div className="share-preview-body">
            <span className="share-preview-title">Join "{room.name}" on GameDate</span>
            <span className="share-preview-note">{note || defaultInviteNote({ room, inviterName })}</span>
            <span className="share-preview-domain">gamedate.co.il</span>
          </div>
        </div>

        <label className="share-note-label" htmlFor="share-note">
          Invitation note
        </label>
        <textarea
          id="share-note"
          className="share-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={280}
          placeholder="Add a personal note…"
        />

        <div className="share-channels">
          <button className="share-channel wa" onClick={handleWhatsApp}>
            <span className="share-channel-icon" aria-hidden="true">🟢</span>
            WhatsApp
          </button>
          <button className="share-channel mail" onClick={handleEmail}>
            <span className="share-channel-icon" aria-hidden="true">✉️</span>
            Email
          </button>
          <button className="share-channel copy" onClick={handleCopy}>
            <span className="share-channel-icon" aria-hidden="true">{copied ? '✓' : '🔗'}</span>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          {canNativeShare && (
            <button className="share-channel native" onClick={handleNativeShare}>
              <span className="share-channel-icon" aria-hidden="true">📤</span>
              More…
            </button>
          )}
        </div>

        <div className="share-link-row" title={link}>
          <code>{link}</code>
        </div>
      </div>
    </div>,
    document.body,
  );
}
